import { formatAdminCount } from "../formatting/format-admin-count";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";

export type AdminJobHotspotCopyInput = {
  failedCount: number;
  jobType: string;
  processingCount?: number;
  queuedCount: number;
  succeededCount: number;
};

function formatJobTypeNarrativeLabel(
  jobType: string,
  locale: AdminDashboardLocale,
): string {
  if (jobType === "account_deletion") {
    return pickAdminDashboardLocaleText(locale, {
      en: "account deletion",
      ko: "계정 삭제",
    });
  }

  if (jobType === "health_file_analysis") {
    return pickAdminDashboardLocaleText(locale, {
      en: "health file analysis",
      ko: "건강 파일 분석",
    });
  }

  if (jobType === "daily_analysis") {
    return pickAdminDashboardLocaleText(locale, {
      en: "daily analysis",
      ko: "일상 분석",
    });
  }

  return jobType;
}

export function formatAdminJobHotspotHeadline(
  input: AdminJobHotspotCopyInput,
  locale: AdminDashboardLocale,
): string {
  if (input.jobType === "account_deletion" && input.failedCount > 0) {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion failed", ko: "삭제 실패" });
  }

  if (
    input.jobType === "account_deletion" &&
    ((input.processingCount ?? 0) > 0 || input.queuedCount > 0)
  ) {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion backlog", ko: "삭제 대기" });
  }

  if (input.failedCount > 0) {
    return pickAdminDashboardLocaleText(locale, {
      en: `${input.jobType} failed`,
      ko: `${input.jobType} 실패`,
    });
  }

  if ((input.processingCount ?? 0) > 0 || input.queuedCount > 0) {
    return pickAdminDashboardLocaleText(locale, {
      en: `${input.jobType} backlog`,
      ko: `${input.jobType} 대기`,
    });
  }

  return pickAdminDashboardLocaleText(locale, {
    en: `${input.jobType} stable`,
    ko: `${input.jobType} 안정`,
  });
}

export function formatAdminJobHotspotSummary(
  input: AdminJobHotspotCopyInput,
  locale: AdminDashboardLocale,
): string {
  const failedCountLabel = formatAdminCount(input.failedCount);
  const queuedCountLabel = formatAdminCount(input.queuedCount);
  const succeededCountLabel = formatAdminCount(input.succeededCount);
  const processingCount = input.processingCount ?? 0;
  const processingCountLabel = formatAdminCount(processingCount);
  const jobTypeLabel = formatJobTypeNarrativeLabel(input.jobType, locale);

  if (processingCount > 0) {
    return locale === "en"
      ? `${jobTypeLabel} currently has ${failedCountLabel} failed, ${processingCountLabel} processing, and ${queuedCountLabel} queued jobs while ${succeededCountLabel} already succeeded.`
      : `${jobTypeLabel} 작업은 현재 실패 ${failedCountLabel}건, 처리 중 ${processingCountLabel}건, 대기 ${queuedCountLabel}건이며 이미 ${succeededCountLabel}건이 성공했습니다.`;
  }

  return locale === "en"
    ? `${jobTypeLabel} currently has ${failedCountLabel} failed and ${queuedCountLabel} queued jobs while ${succeededCountLabel} already succeeded.`
    : `${jobTypeLabel} 작업은 현재 실패 ${failedCountLabel}건, 대기 ${queuedCountLabel}건이며 이미 ${succeededCountLabel}건이 성공했습니다.`;
}
